import { TRANSITION_DURATION, TRANSITION_EASING } from './constants';

const formatMs = <T>(ms: number | T) =>
  typeof ms === 'number' ? `${Math.round(ms)}ms` : ms;

const createTransition = (
  props: string | string[] = ['all'],
  options: { duration?: number; easing?: string; delay?: number } = {},
) => {
  const {
    duration: durationOption = TRANSITION_DURATION,
    easing: easingOption = TRANSITION_EASING,
    delay = 0,
  } = options;

  if (!Array.isArray(props) && typeof props !== 'string') {
    console.error('Transition props should be string or array.');
  }

  const finalProps = Array.isArray(props) ? props : [props];

  return finalProps
    .map((p) => {
      return `${p} ${formatMs(durationOption)} ${easingOption} ${formatMs(
        delay,
      )}`;
    })
    .join(',');
};

/**
 * Converts a nested transition-property structure from theme tokens into a CSS transition string.
 *
 * @example
 * Input structure from theme:
 * {
 *   'transition-duration': '100ms',
 *   'transition-property': {
 *     border: 'border',
 *     'box-shadow': 'box-shadow',
 *     'transition-property': 'border, box-shadow',
 *   },
 *   'transition-timing-function': 'ease',
 *   'transition-delay': '0ms', // optional
 * }
 *
 * Output: 'border 100ms ease 0ms, box-shadow 100ms ease 0ms'
 *
 * @param transitionConfig - The transition configuration object from theme tokens
 * @returns A CSS transition string ready to be used in styled-components
 */
const getTransitionsFromConfig = (transitionConfig: {
  'transition-duration'?: string | number;
  'transition-property'?: Record<string, string> & {
    'transition-property'?: string;
  };
  'transition-timing-function'?: string;
  'transition-delay'?: string;
}): string => {
  const duration = formatMs(
    transitionConfig['transition-duration'] || TRANSITION_DURATION,
  );
  const timingFunction =
    transitionConfig['transition-timing-function'] || TRANSITION_EASING;
  const delay = transitionConfig['transition-delay'] || '0ms';
  const properties = transitionConfig['transition-property'];

  if (!properties) {
    return '';
  }

  // Extract the comma-separated property list
  const propertyList = properties['transition-property'];

  if (!propertyList) {
    return '';
  }

  // Split the property list and create individual transition strings
  const transitionParts = propertyList
    .split(',')
    .map((prop) => prop.trim())
    .filter(Boolean)
    .map((prop) => `${prop} ${duration} ${timingFunction} ${delay}`);

  return transitionParts.join(', ');
};

export default {
  create: createTransition,
  getTransitionsFromConfig,
};
